import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

const api = axios.create({ baseURL: API, withCredentials: true });

// Add token interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('oh_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      setUser(null);
      localStorage.removeItem('oh_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    if (data.token) localStorage.setItem('oh_token', data.token);
    setUser(data);
    return data;
  };

  const register = async (email, password, name, country) => {
    const { data } = await api.post('/auth/register', { email, password, name, country });
    if (data.token) localStorage.setItem('oh_token', data.token);
    setUser(data);
    return data;
  };

  const sendOtp = async (phone, email) => {
    const { data } = await api.post('/auth/send-otp', { phone, email });
    return data;
  };

  const verifyOtp = async (phone, email, otp, name) => {
    const { data } = await api.post('/auth/verify-otp', { phone, email, otp, name });
    if (data.token) localStorage.setItem('oh_token', data.token);
    setUser(data);
    return data;
  };

  const socialAuth = async (provider, email, name) => {
    const { data } = await api.post('/auth/social', { provider, email, name });
    if (data.token) localStorage.setItem('oh_token', data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('oh_token');
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, sendOtp, verifyOtp, socialAuth, logout, refreshUser, api }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { api };
